import axios from 'axios';
import React, { useRef, useEffect, useContext, useState } from 'react';
import { Box, Button, Fade, Icon, IconButton } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { VideoChatContext } from '@/js/shared/context/VideoChatContext';
import { ConferenceContext } from '@/js/shared/context/ConferenceContext';
import UserAvatar from '@/js/shared/components/ui/UserAvatar';
import { getImage, ImageSize } from '../../../shared/utils/imageFormat';

const SponsorItem = React.memo(
  withStyles(() => ({
    carouselHolder: {
      position: 'absolute',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
    },
    backgroundHolder: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      cursor: 'pointer',
      backgroundRepeat: 'no-repeat',
    },
    logoHolder: {
      position: 'absolute',
      left: 5,
      top: 5,
      width: '18%',
      paddingBottom: '18%',
      borderRadius: 5,
      backgroundColor: '#000000',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
    contactHolder: {
      cursor: 'default',
      textAlign: 'left',
      position: 'absolute',
      left: 0,
      bottom: 0,
      borderTopRightRadius: 5,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    title: {
      paddingLeft: 5,
    },
    contactAvatar: {
      padding: '0 5px',
      cursor: 'pointer',
    },
  }))((props) => {
    const { handleSendInvitation } = useContext(VideoChatContext);
    const { openNewThreadPopup, openUserPopup } = useContext(ConferenceContext);

    const gotoEntity = () => {
      window.open(`/live/item/${props.sponsor.id}`, '_blank');
    };
    const noClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const firstVisitor =
      props.showContact && props.sponsor.visitors.length > 0
        ? props.sponsor.visitors.sort(
            (a, b) => a.entityVisitors.order > b.entityVisitors.order
          )[0]
        : null;

    return (
      <Fade in={props.in} timeout={2000} unmountOnExit={true}>
        <Box
          className={props.classes.carouselHolder}
          style={{
            backgroundColor: props.sponsor.bg_color || '#333333',
            backgroundImage: props.sponsor.bg_picture
              ? `url(${getImage(props.sponsor.bg_picture, ImageSize.Medium)})`
              : '',
          }}
          onClick={gotoEntity}
        >
          {props.sponsor.picture && (
            <Box
              className={props.classes.logoHolder}
              style={{
                backgroundImage: props.sponsor.picture
                  ? `url(${getImage(props.sponsor.picture, ImageSize.Small)})`
                  : '',
              }}
            ></Box>
          )}
          {props.showContact && props.sponsor.visitors.length > 0 && (
            <Box className={props.classes.contactHolder} onClick={noClick}>
              <Box display="flex" alignItems="center">
                <Box
                  className={props.classes.contactAvatar}
                  onClick={() => openUserPopup(firstVisitor)}
                >
                  <UserAvatar size="small" user={firstVisitor} />
                </Box>
                <Button
                  onClick={() => openNewThreadPopup([firstVisitor])}
                  startIcon={<Icon>forum</Icon>}
                  size="small"
                >
                  Chat
                </Button>
                {/* <Button
                  onClick={() => handleSendInvitation(firstVisitor)}
                  startIcon={<Icon>videocam</Icon>}
                  size="small"
                >
                  Video
                </Button> */}
              </Box>
            </Box>
          )}
        </Box>
      </Fade>
    );
  })
);

export const SponsorCarousel = React.memo(
  withStyles(() => ({
    holder: {
      position: 'relative',
      height: 100,
      '&:hover > .next': {
        display: 'flex',
      },
    },
    title: {
      paddingTop: 3,
    },
    nextArrow: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
    },
    nextBtn: {
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      color: '#ffffff',
    },
  }))((props) => {
    const timeOut = useRef();
    const showTime = props.data.time ? props.data.time * 1000 : 20000;

    const [sponsors, setSponsors] = useState([]);
    const [showItems, setShowItems] = useState({
      items: [-1, -1], // Pointing to sponsor index
      flip: 0, // Showing item index 0 or 1
      sponsorIndex: -1,
    });

    useEffect(() => {
      try {
        axios
          .get(
            `/api/visitor/entitylists/${props.data.entityListId}/roomsponsors`
          )
          .then((response) => {
            setSponsors(response.data.entities);
          });
      } catch (e) {
        console.log('Sponsor error', e);
      }
    }, []);

    const showNext = () => {
      clearTimeout(timeOut.current);

      setShowItems((lastShowItems) => {
        const nextSponsor = (lastShowItems.sponsorIndex + 1) % sponsors.length;
        const nextFlip = (lastShowItems.flip + 1) % 2;
        const newItems = [...lastShowItems.items];
        newItems[nextFlip] = nextSponsor;

        return {
          items: newItems,
          flip: nextFlip,
          sponsorIndex: nextSponsor,
        };
      });
      timeOut.current = setTimeout(showNext, showTime);
    };

    useEffect(() => {
      if (sponsors.length > 0) {
        showNext();
      }
    }, [sponsors.length]);

    return (
      <Box align="center" className={props.classes.holder}>
        {showItems.items[0] !== -1 && (
          <SponsorItem
            showContact={props.data.contact}
            in={showItems.flip === 0}
            sponsor={sponsors[showItems.items[0]]}
          />
        )}
        {showItems.items[1] !== -1 && (
          <SponsorItem
            showContact={props.data.contact}
            in={showItems.flip === 1}
            sponsor={sponsors[showItems.items[1]]}
          />
        )}
        <Box
          display="none"
          alignItems="center"
          className={`${props.classes.nextArrow} next`}
          onClick={() => showNext()}
        >
          <IconButton className={props.classes.nextBtn} size="small">
            <Icon>navigate_next</Icon>
          </IconButton>
        </Box>
      </Box>
    );
  })
);
